import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BackButton, HomeButton } from '../components/NavButtons';
import ProfilePhotoUploader from '../components/ProfilePhotoUploader';
import ChangePasswordCard from '../components/ChangePasswordCard';
import axios from 'axios';
import {
  ArrowLeft, User, Envelope, Phone, MapPin, Bell, CreditCard, Heart, Star,
  SignOut, Pencil, Buildings, House, MagnifyingGlass, List, CaretRight, Shield, Trash
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser, isSeller, isBuyer } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    preferred_city: user?.preferred_city || '',
    address: user?.address || ''
  });

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/api/users/profile`, formData);
      updateUser(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Update error:', error);
      alert(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = async (newUrl) => {
    try {
      // Upload endpoint already persisted; for manual URL edits we must PUT.
      // We unconditionally PUT to keep both flows simple — endpoint is idempotent.
      const res = await axios.put(`${API_URL}/api/users/profile`, { profile_photo: newUrl || '' });
      updateUser(res.data);
    } catch (error) {
      console.error('Photo update error:', error);
    }
  };

  const handleRoleSwitch = async (newRole, destination) => {
    try {
      await axios.put(`${API_URL}/api/users/profile`, { role: newRole });
      updateUser({ role: newRole });
      navigate(destination || (newRole === 'seller' ? '/seller' : '/buyer'));
    } catch (error) {
      console.error('Role switch error:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">Please sign in to view your profile</h2>
          <Button onClick={() => navigate('/login')} className="bg-blue-500 hover:bg-blue-600 rounded-full">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <BackButton />
            <HomeButton />
          </div>
          <h1 className="text-xl font-bold text-slate-900 font-['Outfit']">Profile</h1>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex flex-col items-center text-center mb-4">
            <ProfilePhotoUploader
              value={user.profile_photo}
              onChange={handlePhotoChange}
              size={96}
            />
            <h2 className="text-xl font-bold text-slate-900 mt-4">{user.name}</h2>
            <p className="text-slate-500">{user.email}</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                user.role === 'both' ? 'bg-purple-100 text-purple-700' :
                user.role === 'seller' ? 'bg-emerald-100 text-emerald-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {user.role === 'both' ? 'Buyer & Seller' : user.role === 'seller' ? 'Seller' : 'Buyer'}
              </span>
              {user.is_verified && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Verified</span>
              )}
            </div>
          </div>
        </div>

        {/* Role Switcher */}
        <div className="bg-white rounded-2xl p-4 border border-slate-100">
          <h3 className="font-semibold text-slate-900 mb-4">Mode</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                if (user.role === 'seller') {
                  handleRoleSwitch('both', '/buyer');
                } else {
                  navigate('/buyer');
                }
              }}
              className={`p-4 rounded-xl border-2 transition-all ${
                isBuyer ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="buyer-mode-btn"
            >
              <MagnifyingGlass size={24} className={isBuyer ? 'text-blue-500' : 'text-slate-400'} />
              <p className={`text-sm font-medium mt-2 ${isBuyer ? 'text-blue-700' : 'text-slate-600'}`}>
                Buyer Mode
              </p>
            </button>
            <button
              onClick={() => {
                if (user.role === 'buyer') {
                  handleRoleSwitch('both', '/seller');
                } else if (isSeller) {
                  navigate('/seller');
                } else {
                  handleRoleSwitch('both', '/seller');
                }
              }}
              className={`p-4 rounded-xl border-2 transition-all ${
                isSeller ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="seller-mode-btn"
            >
              <Buildings size={24} className={isSeller ? 'text-emerald-500' : 'text-slate-400'} />
              <p className={`text-sm font-medium mt-2 ${isSeller ? 'text-emerald-700' : 'text-slate-600'}`}>
                {isSeller ? 'Seller Mode' : 'Become Seller'}
              </p>
            </button>
          </div>
        </div>

        {/* Profile Info */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Personal Information</h3>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              data-testid="edit-profile-btn"
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label htmlFor="city">Preferred City</Label>
                <Input
                  id="city"
                  value={formData.preferred_city}
                  onChange={(e) => setFormData({ ...formData, preferred_city: e.target.value })}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="city-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="mt-2 h-12 rounded-xl"
                  data-testid="address-input"
                />
              </div>
              <Button
                onClick={handleUpdate}
                disabled={loading}
                className="w-full h-12 bg-blue-500 hover:bg-blue-600 rounded-xl"
                data-testid="save-profile-btn"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <ProfileItem icon={User} label="Name" value={user.name} />
              <ProfileItem icon={Envelope} label="Email" value={user.email} />
              <ProfileItem icon={Phone} label="Phone" value={user.phone || 'Not set'} />
              <ProfileItem icon={MapPin} label="City" value={user.preferred_city || 'Not set'} />
              <ProfileItem icon={House} label="Address" value={user.address || 'Not set'} />
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <MenuItem icon={Heart} label="Saved Spaces" onClick={() => navigate('/wishlist')} testId="saved-link" />
          <MenuItem icon={List} label="My Bookings" onClick={() => navigate('/bookings')} testId="bookings-link" />
          <MenuItem icon={Star} label="My Reviews" onClick={() => {}} testId="reviews-link" />
          <MenuItem icon={Bell} label="Notifications" onClick={() => {}} testId="notifications-link" />
          <MenuItem icon={Shield} label="Privacy & Security" onClick={() => {}} testId="privacy-link" />
        </div>

        {/* Change Password */}
        <ChangePasswordCard />

        {/* Admin Link - Only for admins */}
        {(user.role === 'admin' || user.email === 'admin@spacebooking.com') && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <MenuItem icon={Shield} label="Admin Panel" onClick={() => navigate('/admin')} testId="admin-link" />
          </div>
        )}

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <MenuItem icon={SignOut} label="Sign Out" onClick={handleLogout} danger testId="logout-btn" />
        </div>
      </div>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-40">
        <div className="flex items-center justify-around h-16">
          <Link to="/buyer" className="flex flex-col items-center gap-1 text-slate-500">
            <House size={24} />
            <span className="text-xs">Home</span>
          </Link>
          <Link to="/search" className="flex flex-col items-center gap-1 text-slate-500">
            <MagnifyingGlass size={24} />
            <span className="text-xs">Search</span>
          </Link>
          <Link to="/bookings" className="flex flex-col items-center gap-1 text-slate-500">
            <List size={24} />
            <span className="text-xs">Bookings</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 text-blue-600">
            <User size={24} weight="fill" />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

const ProfileItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4">
    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
      <Icon size={20} className="text-slate-500" />
    </div>
    <div className="flex-1">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-medium text-slate-900">{value}</p>
    </div>
  </div>
);

const MenuItem = ({ icon: Icon, label, onClick, danger, testId }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 ${
      danger ? 'text-red-600' : 'text-slate-700'
    }`}
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      <Icon size={20} />
      <span className="font-medium">{label}</span>
    </div>
    <CaretRight size={18} className="text-slate-400" />
  </button>
);

export default ProfilePage;
